import axios from 'axios';
import { UPLOAD_CONFIG } from '../constants/fileStatus';
import { SessionExpiredError } from './SessionExpiredError';

/**
 * Servicio para carga individual de archivos al backend
 * Maneja la subida a Huawei Cloud y almacenamiento en Redis
 */

/**
 * Sube un archivo individual al backend
 * @param {File} file - Archivo a subir
 * @param {string} sessionToken - Token de sesión del trámite
 * @param {number} idDocumentacion - ID del tipo de documentación
 * @param {Function} onProgress - Callback para progreso (0-100)
 * @returns {Promise<Object>} Metadata del archivo subido
 */
export const uploadFile = async (file, sessionToken, idDocumentacion, onProgress) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('idDocumentacion', idDocumentacion);

    const response = await axios.post('/v1/sat/tramites/upload-file', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${sessionToken}`,
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
          onProgress(progress);
        }
      },
    });

    return response.data;
  } catch (error) {
    console.error('Error al subir archivo:', error);

    const statusCode = error.response?.status;
    const message = error.response?.data?.error;
    const isTokenInvalidOrExpired =
      message?.toLowerCase().includes('token') &&
      (message?.toLowerCase().includes('inválido') || message?.toLowerCase().includes('expirado'));

    if (statusCode === 422 && isTokenInvalidOrExpired) {
      throw new SessionExpiredError(statusCode, message);
    }

    throw new Error(message || error.message || 'Error al subir archivo');
  }
};

/**
 * Sube un archivo con reintentos automáticos
 * @param {File} file - Archivo a subir
 * @param {string} sessionToken - Token de sesión del trámite
 * @param {number} idDocumentacion - ID del tipo de documentación
 * @param {Function} onProgress - Callback para progreso
 * @param {number} retryCount - Número de reintento actual
 * @returns {Promise<Object>} Metadata del archivo subido
 */
export const uploadFileWithRetry = async (
  file,
  sessionToken,
  idDocumentacion,
  onProgress,
  retryCount = 0,
) => {
  try {
    return await uploadFile(file, sessionToken, idDocumentacion, onProgress);
  } catch (error) {
    // No reintentar si la sesión expiró
    if (error instanceof SessionExpiredError) {
      throw error;
    }

    // Verificar si debemos reintentar
    const shouldRetry = retryCount < UPLOAD_CONFIG.MAX_RETRIES;
    const isRetriableError = !error.message?.includes('413') && !error.message?.includes('401');

    if (shouldRetry && isRetriableError) {
      // Backoff exponencial: 1s, 2s, 4s
      const delay = UPLOAD_CONFIG.INITIAL_RETRY_DELAY * Math.pow(2, retryCount);

      console.warn(
        `Reintentando subida de archivo (intento ${retryCount + 1}/${
          UPLOAD_CONFIG.MAX_RETRIES
        })...`,
        file.name,
      );

      await new Promise((resolve) => setTimeout(resolve, delay));

      return uploadFileWithRetry(file, sessionToken, idDocumentacion, onProgress, retryCount + 1);
    }

    // No reintentar más
    throw error;
  }
};

/**
 * Marca un archivo como eliminado en el backend
 * @param {string} sessionToken - Token de sesión del trámite
 * @param {string} fileId - ID del archivo a eliminar
 * @returns {Promise<Object>} Confirmación
 */
export const deleteFile = async (sessionToken, fileId) => {
  try {
    const response = await axios.delete(`/v1/sat/tramites/files/${fileId}`, {
      headers: {
        Authorization: `Bearer ${sessionToken}`,
      },
    });

    return response.data;
  } catch (error) {
    console.error('Error al eliminar archivo:', error);

    const statusCode = error.response?.status;
    const message = error.response?.data?.error;
    const isTokenInvalidOrExpired =
      message?.toLowerCase().includes('token') &&
      (message?.toLowerCase().includes('inválido') || message?.toLowerCase().includes('expirado'));

    if (statusCode === 422 && isTokenInvalidOrExpired) {
      throw new SessionExpiredError(statusCode, message);
    }

    throw new Error(message || error.message || 'Error al eliminar archivo');
  }
};
